import { Autocomplete } from '@mui/material';
import { FC, SetStateAction, useCallback, useMemo } from 'react';

import { SearchableSelect, SearchableSelectOption, StyledTextField } from 'components';
import { useLookups, useSizeTypes } from 'contexts';
import { Booking, Chassis, Container, Customer, EquipmentType, Genset, UnitStatus } from 'types';

import { TextField } from 'components/inputs/text-field';
import { InboundUnitSearch, OutboundUnitSearch } from '.';
import { useNewTransaction } from '..';
import { AccountSearch } from '../account';
import { BookingInput } from '../booking';
import { RedeliveryNumber } from '../redelivery';
import { EquipmentProps } from './equipment';
import ManufacturedDate from './manufactured-date';

const ChassisColumn: FC<EquipmentProps> = ({ chassisRef, containerRef, gensetRef, required }) => {
  const { chassis, container, genset, transaction, setChassis, setContainer, setGenset } = useNewTransaction();
  const { unitStatusOptions } = useLookups();
  const { chassisSizeTypeEntries } = useSizeTypes();

  const primaryBooking: Partial<Booking> | undefined | null = useMemo(
    () => container.booking ?? genset.booking,
    [container.booking, genset.booking]
  );

  const sizeTypeOptions: SearchableSelectOption[] = useMemo(() => {
    return chassisSizeTypeEntries.filter(e => e[1].length === 1).map(e => ({ label: e[0], value: e[1][0] }));
  }, [chassisSizeTypeEntries]);

  /* memoize booking change handler to avoid booking input rerender */
  const setBooking = useCallback(
    (args?: SetStateAction<Partial<Booking> | undefined | null>) => {
      setChassis(c => new Chassis({ ...c, booking: typeof args === 'function' ? args(c.booking) : args }));
    },
    [setChassis]
  );

  /* memoize customer change handler to avoid account search rerender */
  const setCustomer = useCallback(
    (customer?: Customer | null) => {
      setChassis(p => new Chassis({ ...p, customer }));
    },
    [setChassis]
  );

  const setUnit = useCallback(
    (args?: SetStateAction<Chassis>) => {
      setChassis(p => new Chassis(typeof args === 'function' ? args(p) : args));
    },
    [setChassis]
  );

  const validateOutboundChassis = useCallback(
    async (unit: Chassis | null) => {
      if (!unit) {
        /* reset genset mounted on chassis (was disabled) */
        if (genset.baseUnit?.equipmentType === EquipmentType.CHASSIS) {
          setGenset(new Genset());
        }
        return;
      }
      if (unit.baseUnit) {
        return `Bulk transaction required. ${unit.unitNumber} mounted on ${unit.baseUnit.unitNumber}`;
      }
      const mountedContainer = unit.mountedUnits?.find(mu => mu.equipmentType === EquipmentType.CONTAINER);
      if (mountedContainer) {
        if (!container.id) {
          setContainer(new Container(mountedContainer));
        } else if (container.id !== mountedContainer.id) {
          return `${mountedContainer.unitNumber} already mounted on ${unit.unitNumber}`;
        } else if (!containerRef.current?.isValid()) {
          containerRef.current?.revalidate();
        }
      }
      const mountedGensets = unit.mountedUnits?.filter(mu => mu.equipmentType === EquipmentType.GENSET);
      if (mountedGensets?.length) {
        if (mountedGensets.length > 1) {
          return `Bulk transaction required. ${mountedGensets.length + 1} gensets mounted on ${unit.unitNumber}`;
        } else if (!genset.id) {
          setGenset(new Genset(mountedGensets[0]));
        } else if (genset.id !== mountedGensets[0].id) {
          return `${mountedGensets[0].unitNumber} already mounted on ${unit.unitNumber}`;
        } else if (!gensetRef.current?.isValid()) {
          gensetRef.current?.revalidate();
        }
      }
    },
    [container.id, containerRef, genset.baseUnit, genset.id, gensetRef, setContainer, setGenset]
  );

  const chassisFieldsRequired: boolean = !chassisRef.current?.isBlank() && !chassis.flatbed && !chassis.ownerChassis;

  return (
    <div className="d-flex flex-column g-inherit">
      <p className="text-primary-dark font-weight-bold">CHASSIS</p>
      <div>
        {chassis.flatbed || chassis.ownerChassis ? (
          <TextField
            label="UNIT NUMBER"
            disabled={!!chassis.flatbed}
            value={chassis.unitNumber ?? ''}
            onChange={unitNumber => setUnit(u => ({ ...u, unitNumber }))}
            required={!!chassis.ownerChassis}
            data-cy="chassis-private-unit-number-input"
          />
        ) : transaction.inbound ? (
          <InboundUnitSearch
            key="chassis"
            textFieldRef={chassisRef}
            required={required}
            setUnit={setUnit}
            unit={chassis}
            equipmentType={EquipmentType.CHASSIS}
            data-cy="chassis-inbound-unit-number-search"
          />
        ) : (
          <OutboundUnitSearch
            textFieldRef={chassisRef}
            required={required}
            unit={chassis}
            setUnit={setUnit}
            customValidation={validateOutboundChassis}
            data-cy="chassis-outbound-unit-number-search"
          />
        )}
      </div>
      <div>
        <SearchableSelect
          containerClass="text-field"
          inputClass={!chassis.sizeType ? 'blank' : ''}
          id="chassis-size-type"
          label="SIZE TYPE"
          disabled={!transaction.inbound && !chassis.flatbed && !chassis.ownerChassis}
          required={chassisFieldsRequired || !!chassis.ownerChassis}
          onClear={() => setUnit(p => ({ ...p, sizeType: undefined }))}
          onValueSelected={(sizeType: any) => setUnit(p => p && { ...p, sizeType })}
          options={sizeTypeOptions}
          value={chassis.sizeType ?? ''}
          data-cy="chassis-size-type-input"
        />
      </div>
      <div>
        <AccountSearch
          required={chassisFieldsRequired || !!chassis.ownerChassis}
          unit={chassis}
          setCustomer={setCustomer}
          data-cy="chassis-account-search"
        />
      </div>
      <div>
        <Autocomplete<UnitStatus | ''>
          fullWidth
          autoHighlight
          size="small"
          disabled={!transaction.inbound}
          options={['', ...unitStatusOptions]}
          value={chassis.unitStatus ?? ''}
          onChange={(_, status) => setChassis(c => new Chassis({ ...c, unitStatus: status || undefined }))}
          getOptionLabel={status => status ?? ''}
          renderInput={params => (
            <StyledTextField
              {...params}
              required={chassisFieldsRequired}
              label="STATUS"
              data-cy="chassis-unit-status-input"
            />
          )}
          renderOption={(props, status) => (
            <li {...props} key={status ?? ''}>
              {status ?? ''}
            </li>
          )}
        />
      </div>
      <BookingInput
        unit={chassis}
        setBooking={setBooking}
        required={chassisFieldsRequired && !primaryBooking && !transaction.inbound}
        disabled={!!chassis.flatbed || !!chassis.ownerChassis}
        data-cy="chassis-booking-search"
      />
      {!!transaction.inbound && (
        <RedeliveryNumber data-cy="chassis-redelivery-number-input" unit={chassis} setUnit={setUnit} />
      )}
      <ManufacturedDate unit={chassis} setUnit={setUnit} />
      <div className="checkboxes d-flex g-3 justify-content-center">
        <div className="d-flex g-2">
          <label className="font-weight-bold">FLTBD / PVT</label>
          <input
            type="checkbox"
            checked={!!chassis.flatbed}
            disabled={!!chassis.ownerChassis || !!chassis.booking}
            onChange={e =>
              setChassis(
                c =>
                  new Chassis({
                    id: null as any,
                    unitNumber: !e.target.checked ? '' : 'FLTBDPVT',
                    customer: c.customer,
                    sizeType: c.sizeType,
                    flatbed: !!e.target.checked
                  })
              )
            }
            data-cy="private-chassis-checkbox"
          />
        </div>
        <div className="d-flex g-2">
          <label className="font-weight-bold">KEEPING</label>
          <input
            type="checkbox"
            checked={!!chassis.ownerChassis}
            disabled={!!chassis.flatbed || !!chassis.booking}
            onChange={e =>
              setChassis(
                c =>
                  new Chassis({
                    id: null as any,
                    unitNumber: c.unitNumber,
                    customer: c.customer,
                    sizeType: c.sizeType,
                    ownerChassis: !!e.target.checked
                  })
              )
            }
            data-cy="owner-chassis-checkbox"
          />
        </div>
      </div>
    </div>
  );
};

export default ChassisColumn;
